/**
 * StatusBadge — canonical status pill.
 *
 * Maps a finite status enum to a themed colour + label chip. This is the
 * CONVERGENCE target for the ≥5 rival status badges scattered across the tree
 * (components/shared/doc-processor/StatusBadge, the web-crawler
 * CrawlJobStatusBadge + ReportJobStatusBadge plugin badges, and the page-level
 * TrainingStatusBadge + JobStatusBadge). Those are NOT migrated in this TASK —
 * they are marked "convergence-pending" in the inventory; this is the single
 * canonical component new code should use.
 *
 * Statuses (variant prop):
 * - success | warning | error | info | neutral
 *
 * Each maps to a status token from theme/colors.ts (no inline colour). Sizes
 * small | medium scale the chip.
 *
 * Accessibility (SPEC-FRONTEND-RULE-007 / RULE-010):
 * - The chip carries `role="status"` so the state is exposed to assistive tech,
 *   labelled by its text. Colour is never the sole carrier of meaning — the
 *   text label always states the status (WCAG 1.4.1).
 */

import React from 'react';
import { Chip } from '@mui/material';
import { colors } from '../../../theme/colors';

export type StatusBadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';

export type StatusBadgeSize = 'small' | 'medium';

export interface StatusBadgeProps {
  /** Status the badge represents. Drives the colour token. */
  status: StatusBadgeVariant;
  /** Human-readable status label (already translated by the caller). */
  label: string;
  /** Chip size. Defaults to `small`. */
  size?: StatusBadgeSize;
  /** Optional leading icon (decorative). */
  icon?: React.ReactElement;
}

const STATUS_COLOR: Record<StatusBadgeVariant, string> = {
  success: colors.status.success,
  warning: colors.status.warning,
  error: colors.status.error,
  info: colors.status.info,
  neutral: colors.text.secondary,
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  size = 'small',
  icon,
}) => {
  const color = STATUS_COLOR[status];

  return (
    <Chip
      role="status"
      aria-label={label}
      label={label}
      size={size}
      icon={icon}
      variant="outlined"
      sx={{
        fontWeight: 600,
        color,
        borderColor: color,
        bgcolor: colors.background.default,
        '& .MuiChip-icon': { color },
      }}
    />
  );
};

export default StatusBadge;
